import type { HTMLAttributes, ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import PulseBar from '@/components/v2/PulseBar';
import JobsIndicator from '@/components/v2/JobsIndicator';
import UserMenu from '@/components/v2/UserMenu';
import LangToggle from '@/components/v2/LangToggle';
import NotificationBell from '@/components/v2/NotificationBell';
import LinkedInLinkControl from '@/components/v2/LinkedInLinkControl';
import BrandWordmark from '@/components/brand/BrandWordmark';
import { useAuth } from '@/contexts/AuthContext';

export interface DesktopNavLink {
  id: string;
  label: string;
  href: string;
  /** render this link as a primary-styled CTA button instead of a text link */
  cta?: boolean;
  /**
   * Section root used to decide the active highlight, when the link opens a
   * page DEEPER than its section (CV opens /cvs/new but owns all of /cvs).
   * Defaults to `href`.
   */
  matchHref?: string;
}

export interface DesktopTopbarProps extends HTMLAttributes<HTMLElement> {
  /** Horizontal nav links shown after the logo (RTL: to the start of avatar) */
  navLinks?: DesktopNavLink[];
  /** Show the JobsIndicator + UserMenu cluster on the end side. Public pages pass false. */
  showAccountCluster?: boolean;
  /** Render the PulseBar under the topbar. Defaults to true. */
  showPulse?: boolean;
  /** Override end-slot content (replaces JobsIndicator + UserMenu). */
  trailing?: ReactNode;
}

function buildDefaultNavLinks(isAr: boolean): DesktopNavLink[] {
  return [
    { id: 'home',     label: isAr ? 'رحلتي' : 'My Journey', href: '/home' },
    { id: 'analyze',  label: isAr ? 'الرادار'   : 'Radar',   href: '/analyze' },
    { id: 'cv',       label: isAr ? 'السيرة'    : 'CV',      href: '/cvs/new', matchHref: '/cvs' },
    { id: 'posts',    label: isAr ? 'المنشورات' : 'Posts',   href: '/posts' },
  ];
}

function DesktopTopbar({
  className,
  navLinks: navLinksProp,
  showAccountCluster = true,
  showPulse = true,
  trailing,
  ...rest
}: DesktopTopbarProps) {
  const [location, navigate] = useLocation();
  const { profile } = useAuth();
  const { i18n } = useTranslation();
  const isAr = (i18n.language || 'ar').startsWith('ar');
  const defaultNavLinks = buildDefaultNavLinks(isAr);
  const navLinks = navLinksProp ?? (
    showAccountCluster && profile?.is_admin
      ? [...defaultNavLinks, { id: 'admin', label: isAr ? 'الإدارة' : 'Admin', href: '/marketing' }]
      : defaultNavLinks
  );

  return (
    <header
      className={cn(
        'sticky top-0 z-30 border-b border-v2-line bg-v2-canvas/95 backdrop-blur-md',
        className,
      )}
      {...rest}
    >
      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-8">
        {/* Start (RTL: right) — Logo + brand + nav links */}
        <div className="flex items-center gap-8">
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="flex items-center gap-2 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 rounded-v2-sm px-1"
            aria-label={isAr ? 'الرئيسية' : 'Home'}
          >
            <BrandWordmark size={22} />
          </button>

          {navLinks.some((l) => !l.cta) && (
            <nav className="flex items-center gap-1" aria-label={isAr ? 'التنقل الرئيسي' : 'Primary navigation'}>
              {navLinks.filter((l) => !l.cta).map((link) => {
                const matchHref = link.matchHref ?? link.href;
                const isActive = location === matchHref || location.startsWith(`${matchHref}/`);
                return (
                  <button
                    key={link.id}
                    type="button"
                    onClick={() => navigate(link.href)}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      'h-9 px-3 rounded-v2-sm font-ar text-[14px] cursor-pointer',
                      'transition-colors duration-200 ease-out',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30',
                      isActive
                        ? 'font-semibold text-teal-700 bg-teal-50'
                        : 'font-medium text-v2-body hover:text-v2-ink hover:bg-v2-canvas-2',
                    )}
                  >
                    {link.label}
                  </button>
                );
              })}
            </nav>
          )}
        </div>

        {/* End (RTL: left) — CTAs from navLinks, then JobsIndicator + UserMenu, or custom trailing */}
        <div className="flex items-center gap-2">
          {trailing ?? (
            <>
              {navLinks.filter((l) => l.cta).map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => navigate(link.href)}
                  className={cn(
                    'inline-flex items-center justify-center h-9 px-4 rounded-v2-sm cursor-pointer',
                    'bg-teal-600 text-white font-ar text-[14px] font-semibold border border-teal-600',
                    'shadow-card transition-colors duration-200 ease-out',
                    'hover:bg-teal-700 active:bg-teal-700',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30',
                  )}
                >
                  {link.label}
                </button>
              ))}
              <LangToggle size="sm" />
              {showAccountCluster && (
                <>
                  {/* Desktop parity: the LinkedIn link/indicator lived only in
                      the mobile Topbar, so it was invisible on desktop. Self
                      contained mode fetches its own state. */}
                  <LinkedInLinkControl />
                  <NotificationBell />
                  <JobsIndicator />
                  <UserMenu />
                </>
              )}
            </>
          )}
        </div>
      </div>
      {showPulse && <PulseBar />}
    </header>
  );
}

export default DesktopTopbar;
